import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardContent,
} from "@mui/material";
import dayjs from "dayjs";
import { createAd, updateAd } from "../../../api/api";

const AdForm = ({ ad, onSave, onCancel }) => {
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        image: null,
        imagePreview: null,
        image_type: "landscape",
        start_date: dayjs().format("YYYY-MM-DD"),
        end_date: dayjs().add(30, "day").format("YYYY-MM-DD"),
        sort_order: 0,
        is_active: true,
        target_url: "",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (ad) {
            setFormData({
                title: ad.title || "",
                description: ad.description || "",
                image: null,
                imagePreview: ad.image_url || null,
                image_type: ad.image_type || "landscape",
                start_date: dayjs(ad.start_date).format("YYYY-MM-DD"),
                end_date: dayjs(ad.end_date).format("YYYY-MM-DD"),
                sort_order: ad.sort_order || 0,
                is_active: ad.is_active !== undefined ? ad.is_active : true,
                target_url: ad.target_url || "",
            });
        }
    }, [ad]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                setError("Image size must be less than 10MB");
                return;
            }

            // Validate file type
            const validTypes = [
                "image/jpeg",
                "image/png",
                "image/jpg",
                "image/gif",
                "image/webp",
            ];
            if (!validTypes.includes(file.type)) {
                setError(
                    "Please select a valid image file (JPEG, PNG, JPG, GIF, WebP)"
                );
                return;
            }

            setFormData((prev) => ({
                ...prev,
                image: file,
                imagePreview: URL.createObjectURL(file),
            }));
            setError(null);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        try {
            // Validate required fields
            if (!formData.title.trim()) {
                throw new Error("Title is required");
            }
            if (!formData.image && !formData.imagePreview) {
                throw new Error("Image is required");
            }
            if (dayjs(formData.start_date).isAfter(dayjs(formData.end_date))) {
                throw new Error("Start date must be before end date");
            }

            const submitData = {
                title: formData.title,
                description: formData.description,
                image_type: formData.image_type,
                start_date: formData.start_date,
                end_date: formData.end_date,
                sort_order: parseInt(formData.sort_order),
                is_active: formData.is_active,
                target_url: formData.target_url,
            };

            if (formData.image) {
                submitData.image = formData.image;
            }

            let response;
            if (ad) {
                response = await updateAd(ad.id, submitData);
            } else {
                response = await createAd(submitData);
            }

            if (response.success) {
                onSave(response.data);
            } else {
                setError(response.message || "Failed to save ad");
            }
        } catch (err) {
            setError(err.message || "Error saving ad");
            console.error("Error saving ad:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            label="Title"
                            value={formData.title}
                            onChange={(e) =>
                                handleInputChange("title", e.target.value)
                            }
                            required
                            sx={{ mb: 3 }}
                        />

                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) =>
                                handleInputChange("description", e.target.value)
                            }
                            multiline
                            rows={3}
                            sx={{ mb: 3 }}
                        />

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Image Type</InputLabel>
                                    <Select
                                        value={formData.image_type}
                                        onChange={(e) =>
                                            handleInputChange(
                                                "image_type",
                                                e.target.value
                                            )
                                        }
                                        label="Image Type"
                                    >
                                        <MenuItem value="landscape">
                                            Landscape
                                        </MenuItem>
                                        <MenuItem value="portrait">
                                            Portrait
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Sort Order"
                                    type="number"
                                    value={formData.sort_order}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "sort_order",
                                            e.target.value
                                        )
                                    }
                                    inputProps={{ min: 0 }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Start Date"
                                    type="date"
                                    value={formData.start_date}
                                    onChange={(e) =>
                                        handleInputChange("start_date", e.target.value)
                                    }
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="End Date"
                                    type="date"
                                    value={formData.end_date}
                                    onChange={(e) =>
                                        handleInputChange("end_date", e.target.value)
                                    }
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            label="Target URL (Optional)"
                            value={formData.target_url}
                            onChange={(e) =>
                                handleInputChange("target_url", e.target.value)
                            }
                            placeholder="https://example.com"
                            sx={{ mb: 3 }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "is_active",
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label="Active"
                            sx={{ mb: 3 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Image Preview
                                </Typography>

                                {formData.imagePreview ? (
                                    <Box sx={{ mb: 2 }}>
                                        <img
                                            src={formData.imagePreview}
                                            alt="Preview"
                                            style={{
                                                width: "100%",
                                                height: "200px",
                                                objectFit: "cover",
                                                borderRadius: "8px",
                                            }}
                                        />
                                    </Box>
                                ) : (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "200px",
                                            border: "2px dashed #ccc",
                                            borderRadius: "8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            mb: 2,
                                        }}
                                    >
                                        <Typography color="text.secondary">
                                            No image selected
                                        </Typography>
                                    </Box>
                                )}

                                <input
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    id="image-upload"
                                    type="file"
                                    onChange={handleImageChange}
                                />
                                <label htmlFor="image-upload">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        fullWidth
                                    >
                                        {formData.imagePreview
                                            ? "Change Image"
                                            : "Select Image"}
                                    </Button>
                                </label>

                                <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{ mt: 1 }}
                                >
                                    Supported formats: JPEG, PNG, JPG, GIF, WebP
                                    <br />
                                    Max size: 10MB
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                        mt: 3,
                    }}
                >
                    <Button onClick={onCancel} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        startIcon={
                            loading ? <CircularProgress size={20} /> : null
                        }
                    >
                        {loading ? "Saving..." : ad ? "Update Ad" : "Create Ad"}
                    </Button>
                </Box>
        </Box>
    );
};

export default AdForm;
